/* eslint-disable react-hooks/exhaustive-deps */
// -----------------------------------------------------------------------------
// UpNextCardV2  (Phase 4a — v2 animation variant)
//
// Drop-in replacement for UpNextCard with two additions:
//   1. Mood gradient — radial from top-left in the action color at 8% opacity,
//      fades in 600ms after card mounts (200ms delay).
//   2. Action-typed icon micro-animation — 1.2s CSS loop, paused on hover,
//      disabled when prefers-reduced-motion is set.
//
// Animation class per action type:
//   EMAIL_SENT / SEND_TREATMENT_PLAN / SEND_REVIEW_REQUEST  → envelope-flap
//   PHONE_CALLED / LOG_PHONE_CALL                           → ring-wobble
//   SCHEDULE_REMINDER                                       → bell-shake
//   SEND_PAYMENT_LINK / CURRENCY_AMOUNT / PAYMENT_METHOD    → coin-flip
//   WHATSAPP_SENT / SMS_SENT                                → pulse-scale
//   SEND_CONSENT_FORM / FREE_TEXT / ADD_NOTE                → pen-tilt
//   (all others)                                            → scale-breathe-v2
//
// The class name is exposed as `data-animation` on the icon wrapper for tests.
// previewMode prop: disabled button, "PREVIEW · UPCOMING" eyebrow, no skip.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import moment from 'moment';
import {
    Box, Typography, Button, Stack, Tooltip, useMediaQuery,
    Dialog, DialogContent, DialogActions,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { StaticDateTimePicker } from '@mui/x-date-pickers/StaticDateTimePicker';
import Iconify from 'src/components/Iconify';
import { CRM_PRIMARY_BUTTON_SX } from 'src/styles/crmButtons';
import { ACTION_META, DEFAULT_ACTION_META } from '../actionMeta';
import TaskAssigneeRow from '../TaskAssigneeRow';
import { EMAIL_ACTION_TYPES } from '../ActionPanel';

const EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';
const APPEAR_MS = 680;

// ---------------------------------------------------------------------------
// Action type → animation class
// ---------------------------------------------------------------------------

const ICON_ANIMATION_CLASS = {
    EMAIL_SENT:           'envelope-flap',
    SEND_TREATMENT_PLAN:  'envelope-flap',
    SEND_REVIEW_REQUEST:  'envelope-flap',
    PHONE_CALLED:         'ring-wobble',
    LOG_PHONE_CALL:       'ring-wobble',
    SCHEDULE_REMINDER:    'bell-shake',
    SEND_PAYMENT_LINK:    'coin-flip',
    CURRENCY_AMOUNT:      'coin-flip',
    PAYMENT_METHOD:       'coin-flip',
    WHATSAPP_SENT:        'pulse-scale',
    SMS_SENT:             'pulse-scale',
    SEND_CONSENT_FORM:    'pen-tilt',
    FREE_TEXT:            'pen-tilt',
    ADD_NOTE:             'pen-tilt',
};
const DEFAULT_ANIMATION_CLASS = 'scale-breathe-v2';

export function getIconAnimationClass(itemType) {
    return ICON_ANIMATION_CLASS[itemType] || DEFAULT_ANIMATION_CLASS;
}

// ---------------------------------------------------------------------------
// Keyframe definitions per animation class
// ---------------------------------------------------------------------------

const KEYFRAME_DEFS = {
    'envelope-flap': {
        '@keyframes envelope-flap': {
            '0%, 100%': { transform: 'rotateY(0deg)' },
            '50%':       { transform: 'rotateY(8deg)' },
        },
    },
    'ring-wobble': {
        '@keyframes ring-wobble': {
            '0%, 100%': { transform: 'rotate(0deg)' },
            '25%':       { transform: 'rotate(-6deg)' },
            '75%':       { transform: 'rotate(6deg)' },
        },
    },
    'bell-shake': {
        '@keyframes bell-shake': {
            '0%, 100%': { transform: 'translateX(0) rotateZ(0deg)' },
            '25%':       { transform: 'translateX(-2px) rotateZ(-3deg)' },
            '75%':       { transform: 'translateX(2px) rotateZ(3deg)' },
        },
    },
    'coin-flip': {
        '@keyframes coin-flip': {
            '0%':   { transform: 'rotateY(0deg)' },
            '50%':  { transform: 'rotateY(180deg)' },
            '100%': { transform: 'rotateY(360deg)' },
        },
    },
    'pulse-scale': {
        '@keyframes pulse-scale': {
            '0%, 100%': { transform: 'scale(1)' },
            '50%':       { transform: 'scale(1.08)' },
        },
    },
    'pen-tilt': {
        '@keyframes pen-tilt': {
            '0%, 100%': { transform: 'rotateZ(0deg)' },
            '25%':       { transform: 'rotateZ(-4deg)' },
            '75%':       { transform: 'rotateZ(4deg)' },
        },
    },
    'scale-breathe-v2': {
        '@keyframes scale-breathe-v2': {
            '0%, 100%': { transform: 'scale(1)' },
            '50%':       { transform: 'scale(1.04)' },
        },
    },
};

function getIconAnimSx(animClass, reducedMotion) {
    if (reducedMotion) return {};
    const kf = KEYFRAME_DEFS[animClass] || KEYFRAME_DEFS['scale-breathe-v2'];
    const kfName = KEYFRAME_DEFS[animClass] ? animClass : 'scale-breathe-v2';
    return {
        ...kf,
        animation: `${kfName} 1.2s ${EASING} infinite`,
        '&:hover': { animationPlayState: 'paused' },
    };
}

// ---------------------------------------------------------------------------
// Primary button labels (same as v1)
// ---------------------------------------------------------------------------

const ACTION_BUTTON_LABELS = {
    EMAIL_SENT:          'Compose email',
    WHATSAPP_SENT:       'Send WhatsApp',
    SMS_SENT:            'Send SMS',
    PHONE_CALLED:        'Log call',
    LOG_PHONE_CALL:      'Log call',
    SEND_CONSENT_FORM:   'Send consent',
    SEND_REVIEW_REQUEST: 'Send review request',
    SEND_PAYMENT_LINK:   'Send payment link',
    SEND_TREATMENT_PLAN: 'Send treatment plan',
    ADD_NOTE:            'Add note',
    SCHEDULE_REMINDER:   'Set reminder',
    LEAD_NEXT_ACTION:    'Set next action',
    BOOK_APPOINTMENT:    'Book appointment',
    CONFIRMATION:        'Mark confirmed',
    UPLOAD_DOCUMENT:     'Upload document',
    LINKED_CHECKLIST:    'Open checklist',
    FREE_TEXT:           'Enter text',
    MULTIPLE_CHOICE:     'Choose option',
    YES_NO_TOGGLE:       'Mark done',
    CURRENCY_AMOUNT:     'Enter amount',
    PAYMENT_METHOD:      'Set method',
    STAFF_PICKER:        'Assign staff',
    DATE_FIELD:          'Set date',
};

// Returns a { label, color } chip for the "next up" due-date display.
function getNextDueChip(item) {
    if (!item?.due_at) return null;
    const d = new Date(item.due_at);
    const now = new Date();
    const eot = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    if (d < now) return { label: 'Overdue', color: '#FF5630' };
    if (d <= eot) return { label: 'Due today', color: '#FFAB00' };
    return { label: d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }), color: '#6e6e73' };
}

// ---------------------------------------------------------------------------
// Eyebrow helper (same logic as v1)
// ---------------------------------------------------------------------------

function getEyebrow(item) {
    if (!item?.due_at) return { label: 'UP NEXT', color: '#6e6e73' };
    const now = new Date();
    const due = new Date(item.due_at);
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    if (due < now) return { label: 'OVERDUE', color: '#FF5630' };
    if (due <= endOfToday) return { label: 'UP NEXT · DUE TODAY', color: '#FFAB00' };
    return { label: 'UP NEXT', color: '#6e6e73' };
}

// ---------------------------------------------------------------------------

export default function UpNextCardV2({ item, onActionClick, onSkip, onPause, leadEmail, onChangeDueDate, nextItem, nextEmptyReason, onItemClick, practiceId, panelOpen }) {
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const [appeared, setAppeared] = useState(false);
    // Shared due-date dialog — tracks which item (featured or next-up) is being edited.
    const [calItem, setCalItem] = useState(null);
    const [calValue, setCalValue] = useState(null);

    useEffect(() => {
        const t = setTimeout(() => setAppeared(true), 16);
        return () => clearTimeout(t);
    }, []);

    const handleOpenDueDate = (targetItem) => {
        setCalValue(targetItem?.due_at ? moment(targetItem.due_at) : null);
        setCalItem(targetItem || item);
    };
    const handleDueDateCancel = () => setCalItem(null);
    const handleDueDateConfirm = () => {
        if (calValue && onChangeDueDate && calItem) {
            onChangeDueDate(calItem, calValue.toDate());
        }
        setCalItem(null);
    };
    // Single-click confirm for preset chips — saves immediately, no extra "Save" click needed.
    const handlePresetSelect = (date) => {
        if (onChangeDueDate && calItem) {
            onChangeDueDate(calItem, date.toDate());
        }
        setCalItem(null);
    };

    if (!item) return null;

    const meta = ACTION_META[item.item_type] || DEFAULT_ACTION_META;
    const eyebrow = getEyebrow(item);
    const title = item.title || item.name || meta.label;
    const isEmailType = EMAIL_ACTION_TYPES.has(item.item_type);
    // Only block when the orchestrator explicitly passes an empty leadEmail string.
    // If leadEmail is undefined (prop absent), the guard is inactive.
    const noEmailBlocked = isEmailType && leadEmail !== undefined && !leadEmail;
    let buttonLabel = ACTION_BUTTON_LABELS[item.item_type] || 'Take action';
    if (noEmailBlocked) buttonLabel = 'Add email first';
    const isOverdue = eyebrow.label === 'OVERDUE';

    const animClass = getIconAnimationClass(item.item_type);
    const iconAnimSx = getIconAnimSx(animClass, reducedMotion);

    // ---------------------------------------------------------------------------
    // Card entrance: v2 uses slide-up + scale (vs v1's simple fade)
    // ---------------------------------------------------------------------------
    const cardEntranceSx = reducedMotion
        ? { opacity: appeared ? 1 : 0 }
        : {
            '@keyframes upnext-enter-v2': {
                from: { opacity: 0, transform: 'translateY(24px) scale(0.95)' },
                to:   { opacity: 1, transform: 'translateY(0) scale(1)' },
            },
            animation: `upnext-enter-v2 ${APPEAR_MS}ms ${EASING} forwards`,
        };

    // Right pane — "Next up" preview or "All caught up" placeholder
    const showRightPane = !!(nextItem || nextEmptyReason);
    // Left column flex — '1 1 auto' when stacked (panelOpen), else 70% or full-width
    const leftColFlex = panelOpen ? '1 1 auto' : (showRightPane ? '0 0 calc(70% - 9px)' : '1'); // eslint-disable-line no-nested-ternary
    const nextMeta = nextItem ? (ACTION_META[nextItem.item_type] || DEFAULT_ACTION_META) : null;
    const nextTitle = nextItem ? (nextItem.title || nextItem.name || nextMeta.label) : null;
    const nextDueChip = getNextDueChip(nextItem);
    const nextPaneSx = nextItem && !reducedMotion
        ? {
            '@keyframes next-slide-in-v2': {
                from: { opacity: 0, transform: 'translateX(-8px)' },
                to:   { opacity: 1, transform: 'translateX(0)' },
            },
            animation: 'next-slide-in-v2 340ms cubic-bezier(0.32, 0.72, 0, 1) forwards',
        }
        : {};

    // OVERDUE dot pulse (same as v1)
    const overdueDotsxProps = isOverdue && !reducedMotion
        ? {
            '@keyframes overdue-pulse': {
                '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                '50%':       { transform: 'scale(1.4)', opacity: 0.4 },
            },
            animation: 'overdue-pulse 1.4s ease-in-out infinite',
        }
        : {};

    return (
        <Box
            data-testid="upnext-card-v2"
            sx={{
                position: 'relative',
                overflow: 'hidden',
                bgcolor: '#ffffff',
                borderRadius: '16px',
                p: 3,
                minHeight: 160,
                boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)',
                ...cardEntranceSx,
            }}
        >
            {/* Mood gradient overlay — radial from top-left in action color */}
            <Box
                data-testid="mood-gradient"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '16px',
                    background: `radial-gradient(at top left, ${meta.color}14 0%, transparent 60%)`,
                    pointerEvents: 'none',
                    ...(reducedMotion
                        ? {}
                        : {
                            '@keyframes mood-fade': {
                                from: { opacity: 0 },
                                to:   { opacity: 1 },
                            },
                            opacity: 0,
                            animation: appeared
                                ? 'mood-fade 600ms 200ms cubic-bezier(0.32, 0.72, 0, 1) forwards'
                                : 'none',
                        }),
                }}
            />

            {/* Left accent strip (same as v1) */}
            <Box
                data-testid="upnext-accent-strip-v2"
                sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    bgcolor: meta.color,
                    borderRadius: '16px 0 0 16px',
                }}
            />

            {/* 70 / 30 split row — stacks vertically when action panel is open */}
            <Box sx={{ display: 'flex', flexDirection: panelOpen ? 'column' : 'row', alignItems: 'stretch', gap: 2 }}>

            {/* ── Left column (featured task) ─────────────────────────── */}
            {/* overflow:hidden clips any child that expands wider than the 70%
                allocation (e.g. action-row buttons on narrow panels). */}
            <Box sx={{ flex: leftColFlex, width: panelOpen ? '100%' : 'auto', minWidth: 0, overflow: 'hidden' }}>

            {/* Eyebrow */}
            <Box
                data-testid="upnext-eyebrow-v2"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}
            >
                <Box
                    data-testid="upnext-eyebrow-dot-v2"
                    data-overdue={isOverdue ? 'true' : undefined}
                    sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        bgcolor: eyebrow.color,
                        flexShrink: 0,
                        ...overdueDotsxProps,
                    }}
                />
                <Typography
                    component="span"
                    sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: eyebrow.color,
                    }}
                >
                    {eyebrow.label}
                </Typography>
            </Box>

            {/* Icon + Title */}
            <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 2 }}>
                <Box
                    data-testid="upnext-icon-v2"
                    data-animation={reducedMotion ? 'none' : animClass}
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '14px',
                        bgcolor: `${meta.color}1A`,
                        border: `1.5px solid ${meta.color}33`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        mt: 0.25,
                        ...iconAnimSx,
                    }}
                >
                    <Iconify icon={meta.icon} width={28} height={28} sx={{ color: meta.color }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: '22px',
                            fontWeight: 700,
                            color: '#1d1d1f',
                            letterSpacing: '-0.022em',
                            lineHeight: 1.25,
                            // Allow 2-line wrap for long titles like "1-MONTH FROM CONSULTATION".
                            // wordBreak:'break-word' handles hyphenated strings without spaces.
                            wordBreak: 'break-word',
                        }}
                    >
                        {title}
                    </Typography>
                    {item.description && (
                        <Typography
                            sx={{
                                fontSize: '13px',
                                color: '#3c3c43',
                                letterSpacing: '-0.005em',
                                mt: 0.5,
                                lineHeight: 1.4,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {item.description}
                        </Typography>
                    )}
                    {item?.due_at && (
                        <Typography
                            data-testid="upnext-featured-due-timestamp-v2"
                            sx={{
                                fontSize: '12px',
                                fontWeight: 500,
                                color: '#86868b',
                                letterSpacing: '-0.005em',
                                mt: 0.75,
                            }}
                        >
                            {moment(item.due_at).format('dddd D MMMM, HH:mm')}
                        </Typography>
                    )}
                </Box>
            </Stack>

            {/* Action row — flexWrap lets buttons move to a second line on narrow panes. */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: '8px' }}>
                <Tooltip
                    title={noEmailBlocked ? 'This lead has no email on file' : ''}
                    placement="top"
                    arrow
                    disableHoverListener={!noEmailBlocked}
                >
                    <span>
                        <Button
                            data-testid="upnext-action-btn-v2"
                            variant="contained"
                            disableElevation
                            disabled={noEmailBlocked}
                            endIcon={!noEmailBlocked && <Iconify icon="mdi:arrow-right" width={16} height={16} />}
                            onClick={!noEmailBlocked ? () => onActionClick && onActionClick(item) : undefined}
                            sx={{
                                ...CRM_PRIMARY_BUTTON_SX,
                                fontSize: '14px',
                                fontWeight: 700,
                                minWidth: 140,
                                py: 1,
                            }}
                        >
                            {buttonLabel}
                        </Button>
                    </span>
                </Tooltip>
                <Typography
                    data-testid="upnext-skip-btn-v2"
                    component="button"
                    onClick={() => onSkip && onSkip(item)}
                    sx={{
                        fontSize: '13px',
                        color: '#6e6e73',
                        letterSpacing: '-0.01em',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        p: 0,
                        fontFamily: 'inherit',
                        '&:hover': { color: '#1d1d1f', textDecoration: 'underline' },
                    }}
                >
                    Skip for now
                </Typography>
                {onPause && (
                    <Typography
                        data-testid="upnext-pause-btn-v2"
                        component="button"
                        onClick={() => onPause(item)}
                        sx={{
                            fontSize: '13px',
                            color: '#6e6e73',
                            letterSpacing: '-0.01em',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            p: 0,
                            fontFamily: 'inherit',
                            '&:hover': { color: '#FFAB00', textDecoration: 'underline' },
                        }}
                    >
                        Pause
                    </Typography>
                )}
                {onChangeDueDate && (
                    <Button
                        data-testid="upnext-change-due-date-btn-v2"
                        size="small"
                        aria-label="Change due date"
                        startIcon={<Iconify icon="mdi:calendar-edit" width={14} height={14} />}
                        onClick={() => handleOpenDueDate(item)}
                        sx={{
                            color: '#6e6e73',
                            fontSize: '13px',
                            fontWeight: 500,
                            textTransform: 'none',
                            letterSpacing: '-0.01em',
                            bgcolor: 'transparent',
                            borderRadius: '6px',
                            py: '2px',
                            px: '8px',
                            minWidth: 0,
                            '&:hover': { color: '#1d1d1f', bgcolor: 'rgba(0,0,0,0.04)' },
                        }}
                    >
                        Change due date
                    </Button>
                )}
            </Stack>

            {/* Assignee row — editable, shown when task has a taskId */}
            {item && item.taskId && practiceId && (
                <Box sx={{ mt: 1.25 }}>
                    <TaskAssigneeRow
                        data-testid="featured-task-assignee-row"
                        practiceId={practiceId}
                        taskId={item.taskId}
                        initialAssignees={item.rawTask?.assignees || []}
                        size={24}
                    />
                </Box>
            )}
            </Box>{/* /left column */}

            {/* ── Vertical divider (hidden when stacked) ──────────────── */}
            {showRightPane && !panelOpen && (
                <Box
                    aria-hidden="true"
                    sx={{ width: '1px', bgcolor: 'rgba(0,0,0,0.06)', flexShrink: 0 }}
                />
            )}

            {/* ── Right column (next up preview) ──────────────────────── */}
            {nextItem && (
                <Box
                    data-testid="upnext-next-pane-v2"
                    role="button"
                    tabIndex={0}
                    aria-label={`Next up: ${nextTitle}`}
                    onClick={() => onItemClick && onItemClick(nextItem)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { if (onItemClick) onItemClick(nextItem); } }}
                    sx={{
                        flex: panelOpen ? '1 1 auto' : '0 0 calc(30% - 9px)',
                        width: panelOpen ? '100%' : 'auto',
                        minWidth: panelOpen ? 0 : '80px',
                        mt: panelOpen ? 1.5 : 0,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: 1,
                        cursor: onItemClick ? 'pointer' : 'default',
                        borderRadius: '8px',
                        px: 1,
                        py: 0.5,
                        '&:hover': onItemClick ? { bgcolor: 'rgba(0,0,0,0.025)' } : {},
                        ...nextPaneSx,
                    }}
                >
                    <Typography
                        data-testid="upnext-next-eyebrow-v2"
                        component="span"
                        sx={{
                            fontSize: '10px',
                            fontWeight: 700,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: '#6e6e73',
                        }}
                    >
                        Next up
                    </Typography>
                    <Box
                        data-testid="upnext-next-icon-v2"
                        sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '6px',
                            bgcolor: `${nextMeta.color}1A`,
                            border: `1px solid ${nextMeta.color}33`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <Iconify icon={nextMeta.icon} width={13} height={13} sx={{ color: nextMeta.color }} />
                    </Box>
                    <Typography
                        data-testid="upnext-next-title-v2"
                        sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#1d1d1f',
                            letterSpacing: '-0.01em',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {nextTitle}
                    </Typography>
                    {nextDueChip && (
                        <Typography
                            data-testid="upnext-next-due-chip-v2"
                            sx={{
                                fontSize: '11px',
                                fontWeight: 500,
                                color: nextDueChip.color,
                                letterSpacing: '-0.005em',
                            }}
                        >
                            {nextDueChip.label}
                        </Typography>
                    )}

                    {/* Absolute timestamp — precision companion to the relative chip */}
                    {nextItem?.due_at && (
                        <Typography
                            data-testid="upnext-next-due-timestamp-v2"
                            sx={{
                                fontSize: '11px',
                                fontWeight: 400,
                                color: '#86868b',
                                letterSpacing: '-0.005em',
                            }}
                        >
                            {moment(nextItem.due_at).format('ddd D MMM, HH:mm')}
                        </Typography>
                    )}

                    {/* Change due date button for next-up item */}
                    {onChangeDueDate && (
                        <Button
                            data-testid="upnext-next-change-due-date-btn-v2"
                            size="small"
                            aria-label="Change next task due date"
                            startIcon={<Iconify icon="mdi:calendar-edit" width={12} height={12} />}
                            onClick={(e) => { e.stopPropagation(); handleOpenDueDate(nextItem); }}
                            sx={{
                                color: '#6e6e73',
                                fontSize: '11px',
                                fontWeight: 500,
                                textTransform: 'none',
                                letterSpacing: '-0.005em',
                                bgcolor: 'transparent',
                                borderRadius: '6px',
                                py: '1px',
                                px: '6px',
                                minWidth: 0,
                                alignSelf: 'flex-start',
                                mt: 0.25,
                                '&:hover': { color: '#1d1d1f', bgcolor: 'rgba(0,0,0,0.04)' },
                            }}
                        >
                            Change due date
                        </Button>
                    )}

                    {/* Read-only assignee row for next-up item */}
                    {nextItem?.taskId && practiceId && nextItem?.rawTask?.assignees?.length > 0 && (
                        <Box onClick={(e) => e.stopPropagation()}>
                            <TaskAssigneeRow
                                data-testid="next-task-assignee-row-v2"
                                practiceId={practiceId}
                                taskId={nextItem.taskId}
                                initialAssignees={nextItem.rawTask.assignees}
                                size={18}
                                readOnly
                            />
                        </Box>
                    )}
                </Box>
            )}

            {/* ── Right column — "All caught up" placeholder ──────────── */}
            {!nextItem && nextEmptyReason === 'caught_up' && (
                <Box
                    data-testid="upnext-next-pane-empty-v2"
                    aria-label="All caught up — nothing else queued"
                    sx={{
                        flex: panelOpen ? '1 1 auto' : '0 0 calc(30% - 9px)',
                        width: panelOpen ? '100%' : 'auto',
                        minWidth: panelOpen ? 0 : '80px',
                        mt: panelOpen ? 1.5 : 0,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: 0.75,
                        px: 1,
                        py: 0.5,
                        opacity: 0.5,
                    }}
                >
                    <Typography
                        component="span"
                        sx={{
                            fontSize: '10px',
                            fontWeight: 700,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: '#6e6e73',
                        }}
                    >
                        Next up
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Iconify icon="mdi:check-circle-outline" width={14} height={14} sx={{ color: '#34c759', flexShrink: 0 }} />
                        <Typography
                            sx={{
                                fontSize: '12px',
                                fontWeight: 500,
                                color: '#6e6e73',
                                letterSpacing: '-0.005em',
                            }}
                        >
                            All caught up
                        </Typography>
                    </Box>
                </Box>
            )}

            </Box>{/* /split row */}

            {/* Due-date change dialog — shared for featured and next-up items */}
            <Dialog
                data-testid="upnext-due-date-dialog-v2"
                open={calItem != null}
                onClose={handleDueDateCancel}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
            >
                <Box sx={{ px: 3, pt: 2.5, pb: 1.5, bgcolor: '#fafafa', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6e6e73', mb: 0.5 }}>
                        Reschedule task
                    </Typography>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.02em', lineHeight: 1.25 }}>
                        {calItem?.title || 'Change due date'}
                    </Typography>
                    {calItem?.due_at && (
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1 }}>
                            <Iconify icon="mdi:calendar-clock" width={14} height={14} sx={{ color: '#6e6e73' }} />
                            <Typography sx={{ fontSize: '12px', color: '#6e6e73', letterSpacing: '-0.005em' }}>
                                Currently due {moment(calItem.due_at).format('ddd D MMM, HH:mm')}
                            </Typography>
                        </Stack>
                    )}
                </Box>
                <DialogContent sx={{ p: 0 }}>
                    {/* Quick presets */}
                    <Stack direction="row" spacing={0.75} sx={{ px: 3, pt: 2, pb: 1.5, flexWrap: 'wrap', rowGap: 0.75 }}>
                        {[
                            { label: 'Today', getDate: () => moment().endOf('day') },
                            { label: 'Tomorrow', getDate: () => moment().add(1, 'day').endOf('day') },
                            { label: 'In 3 days', getDate: () => moment().add(3, 'days').endOf('day') },
                            { label: 'Next week', getDate: () => moment().add(1, 'week').endOf('day') },
                            { label: 'In 2 weeks', getDate: () => moment().add(2, 'weeks').endOf('day') },
                        ].map((preset) => (
                            // Single-click confirm: presets save immediately without needing "Save" button.
                            // Calendar date picks still stage into calValue and require "Save".
                            <Button
                                key={preset.label}
                                size="small"
                                onClick={() => handlePresetSelect(preset.getDate())}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    borderRadius: '8px',
                                    py: 0.4,
                                    px: 1.25,
                                    minWidth: 0,
                                    color: '#3c3c43',
                                    bgcolor: 'rgba(0,0,0,0.04)',
                                    '&:hover': { bgcolor: 'rgba(7,141,238,0.10)', color: '#078DEE' },
                                }}
                            >
                                {preset.label}
                            </Button>
                        ))}
                    </Stack>
                    <Box sx={{ px: 1, pb: 1 }}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <StaticDateTimePicker
                                displayStaticWrapperAs="desktop"
                                ampm={false}
                                value={calValue}
                                onChange={(newVal) => setCalValue(newVal)}
                                slotProps={{
                                    actionBar: { actions: [] },
                                    toolbar: { hidden: true },
                                    textField: { 'data-testid': 'upnext-due-date-picker-input-v2' },
                                }}
                                sx={{
                                    bgcolor: 'transparent',
                                    '& .MuiPickersDay-root.Mui-selected': { bgcolor: '#078DEE', '&:hover': { bgcolor: '#0a7bd1' } },
                                    '& .MuiPickersDay-today': { borderColor: '#078DEE' },
                                }}
                            />
                        </LocalizationProvider>
                    </Box>
                    {calValue && (
                        <Box sx={{ px: 3, pb: 1.5 }}>
                            <Typography sx={{ fontSize: '12px', color: '#6e6e73', letterSpacing: '-0.005em' }}>
                                New due date:{' '}
                                <Box component="span" sx={{ color: '#078DEE', fontWeight: 600 }}>
                                    {calValue.format('dddd D MMMM YYYY, HH:mm')}
                                </Box>
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 1.75, borderTop: '1px solid rgba(0,0,0,0.06)', bgcolor: '#fafafa' }}>
                    <Button
                        data-testid="upnext-due-date-cancel-v2"
                        onClick={handleDueDateCancel}
                        sx={{ color: '#6e6e73', textTransform: 'none', fontSize: '14px', fontWeight: 500 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        data-testid="upnext-due-date-confirm-v2"
                        variant="contained"
                        disableElevation
                        disabled={!calValue}
                        onClick={handleDueDateConfirm}
                        sx={{ ...CRM_PRIMARY_BUTTON_SX, textTransform: 'none', fontSize: '14px', fontWeight: 600, py: 0.75, px: 2.5, borderRadius: '8px' }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

UpNextCardV2.propTypes = {
    item: PropTypes.object,
    onActionClick: PropTypes.func,
    onSkip: PropTypes.func,
    /** Called with item when user clicks "Pause" — opens pause dialog in parent. */
    onPause: PropTypes.func,
    /** Lead's email — when empty and item is an email type, CTA is blocked. */
    leadEmail: PropTypes.string,
    /** Called with (item, newDate: Date) when user confirms a due-date change. */
    onChangeDueDate: PropTypes.func,
    /** The next-in-queue checklist item to preview in the right 30% pane. */
    nextItem: PropTypes.object,
    /** When nextItem is null, reason the pane is empty — 'caught_up' renders placeholder. */
    nextEmptyReason: PropTypes.oneOf(['caught_up']),
    /** Called with nextItem when user clicks the right pane to promote it. */
    onItemClick: PropTypes.func,
    /** Practice ID forwarded to TaskAssigneeRow for PATCH calls. */
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** When true (action panel open), the Next-up pane stacks below instead of the 30% side column. */
    panelOpen: PropTypes.bool,
};

UpNextCardV2.defaultProps = {
    item: null,
    onActionClick: null,
    onSkip: null,
    onPause: null,
    leadEmail: undefined,
    onChangeDueDate: null,
    nextItem: null,
    nextEmptyReason: null,
    onItemClick: null,
    practiceId: null,
    panelOpen: false,
};
